import { useState, useEffect } from "react";
import axiosInstance from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { canAccessApplications } from "../../utils/princetonAccess";

const DashboardButton = ({ email, status, schoolEmailVerified = false }) => {
  const { authState } = useAuth();
  const [waitlistSelection, setWaitlistSelection] =
    useState("waitlist_confirmed");
  const [attendanceSelection, setAttendanceSelection] = useState("confirmed");
  const [hackathonStatus, setHackathonStatus] = useState("not_open");
  const [submissionError, setSubmissionError] = useState("");
  const canEditApplication = canAccessApplications(email, hackathonStatus);

  const handleWaitlistSelectChange = (event) => {
    setWaitlistSelection(event.target.value);
    setSubmissionError("");
  };

  const handleAttendanceSelectChange = (event) => {
    // console.log(event.target.value);
    setAttendanceSelection(event.target.value);
    setSubmissionError("");
  };

  const handleWaitlistSubmit = async (event) => {
    event.preventDefault();
    setSubmissionError("");

    // console.log("Attendance Selection: " + waitlistSelection);

    if (waitlistSelection === "waitlist_confirmed") {
      try {
        // console.log("Confirming spot on waitlist");
        await axiosInstance.post(
          `/api/applications/confirm-waitlist`
        );
        // console.log("Response:", response.data);
        window.location.reload(true);
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmissionError(
          error.response?.data?.message ||
            "We couldn't update your waitlist response. Please try again."
        );
      }
    } else if (waitlistSelection === "declined") {
      try {
        // console.log("Declining attendance");
        await axiosInstance.post(`/api/applications/decline`);
        // console.log("Response:", response.data);
        window.location.reload(true);
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmissionError(
          error.response?.data?.message ||
            "We couldn't update your waitlist response. Please try again."
        );
      }
    }
  };

  const handleAttendanceSubmit = async (event) => {
    event.preventDefault();
    setSubmissionError("");
    // console.log("Attendance Selection: " + attendanceSelection);

    if (attendanceSelection === "confirmed") {
      if (!schoolEmailVerified) {
        setSubmissionError("Verify your school email above before confirming attendance.");
        return;
      }
      try {
        // console.log("Confirming attendance");
        await axiosInstance.post("/api/applications/confirm");
        // console.log("Response:", response.data);
        window.location.reload(true);
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmissionError(
          error.response?.data?.message ||
            "We couldn't save your attendance confirmation. Please try again."
        );
      }
    } else if (attendanceSelection === "declined") {
      // console.log("Here");
      try {
        // console.log("Declining attendance");
        await axiosInstance.post(`/api/applications/decline`);
        // console.log("Response:", response.data);
        window.location.reload(true);
      } catch (error) {
        setSubmissionError(
          error.response?.data?.message ||
            "We couldn't save your attendance decision. Please try again."
        );
      }
    }
  };

  useEffect(() => {
    const fetchHackathonStatus = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/applications/cycle/status"
        );
        // console.log(response);
        setHackathonStatus(response.data);
      } catch (err) {
        // console.log(err);
      }
    };

    if (authState.isAuthenticated) {
      fetchHackathonStatus();
    }
  }, [authState.isAuthenticated]);

  if (
    (status === "unsubmitted" || status === "started") &&
    canEditApplication
  ) {
    return (
      <div className="app-button-pad">
        <a href="/apply" className="apply-button">
          Launch Application →
        </a>
      </div>
    );
  } else if (
    status === "waitlisted" &&
    (hackathonStatus === "released" ||
      hackathonStatus === "confirmation_closed")
  ) {
    return (
      <form className="dashboard-waitlist-form" onSubmit={handleWaitlistSubmit}>
        <label className="dashboard-waitlist-label">
          Would you like to be placed on the waitlist for HackPrinceton?
        </label>
        <select
          className="dashboard-waitlist-select"
          onChange={handleWaitlistSelectChange}
          required
        >
          <option value="waitlist_confirmed">
            Yes, I would like to be on the waitlist.
          </option>
          <option value="declined">
            No, I would not like to be on the waitlist.
          </option>
        </select>
        <button type="submit" className="dashboard-waitlist-button">
          Submit
        </button>
        {submissionError && (
          <p className="dashboard-waitlist-error">{submissionError}</p>
        )}
      </form>
    );
  } else if (
    (status === "accepted" && hackathonStatus === "released") ||
    (status === "waitlist_accepted" &&
      (hackathonStatus === "released" ||
        hackathonStatus === "confirmation_closed" ||
        hackathonStatus === "waitlist_closed"))
  ) {
    if (!schoolEmailVerified) {
      return (
        <div>
          <p>To participate in HackPrinceton, you must confirm your attendance through this portal.</p>
          <p>Verify your school email above to open the attendance confirmation form.</p>
          <button type="button" className="dashboard-waitlist-button" onClick={async () => {
            if (!window.confirm("Decline your place at HackPrinceton?")) return;
            try {
              await axiosInstance.post("/api/applications/decline");
              window.location.reload();
            } catch (error) {
              setSubmissionError(error.response?.data?.message || "Could not decline attendance. Please try again.");
            }
          }}>Decline attendance</button>
          {submissionError && <p role="alert">{submissionError}</p>}
        </div>
      );
    }
    return (
      <form
        className="dashboard-waitlist-form"
        onSubmit={handleAttendanceSubmit}
      >
        <p id="attendance-instructions">
          To participate in HackPrinceton, you must confirm your attendance through this portal. To confirm, select "Yes, I'm looking forward to it!" and click "Submit".
        </p>
        <label className="dashboard-waitlist-label" htmlFor="attendance-response">
          Will you be able to attend HackPrinceton?
        </label>
        <select
          id="attendance-response"
          aria-describedby="attendance-instructions"
          className="dashboard-waitlist-select"
          value={attendanceSelection}
          onChange={handleAttendanceSelectChange}
          required
        >
          <option value="confirmed">Yes, I'm looking forward to it!</option>
          <option value="declined">
            No, unfortunately I can no longer attend.
          </option>
        </select>
        <button type="submit" className="dashboard-waitlist-button">
          Submit
        </button>
        {submissionError && (
          <p className="dashboard-waitlist-error">{submissionError}</p>
        )}
      </form>
    );
  }

  return <div></div>;
};

export default DashboardButton;
